import { useFragment } from "@/generated";
import { EditLessonNameDocument, LessonFieldsFragmentDoc } from "@/generated/graphql";
import { useMutation } from "@apollo/client/react";

/**
 * Hook for updating an existing lesson's name.
 *
 * Provides a mutation function to update a lesson's name and automatically
 * refetches lesson queries to keep the UI synchronized with the latest lesson data.
 *
 * @returns An object containing:
 *   - editLessonName: Async function to update a lesson's name. Takes lesson ID and name string, returns the updated lesson.
 *   - loading: Boolean indicating if the mutation is in progress
 *
 * @example
 * const { editLessonName, loading } = useEditLessonName();
 * const updated = await editLessonName("lesson123", "Updated Lesson Name");
 */
export function useEditLessonName() {
  const [editLessonNameMutation, { loading }] = useMutation(EditLessonNameDocument);

  const editLessonName = async (lessonId: string, name: string) => {
    const result = await editLessonNameMutation({
      variables: { lessonId, name },
    });

    const maskedLesson = result.data?.editLessonName;
    const updatedLesson = maskedLesson
      ? useFragment(LessonFieldsFragmentDoc, maskedLesson)
      : undefined;

    return updatedLesson;
  };

  return {
    editLessonName,
    loading,
  };
}
